const Joi = require('joi');
const math = require('mathjs');
const zeros = require('zeros');
const ndgemm = require('ndarray-gemm');
const ndunpack = require('ndarray-unpack');

/**
 * @param {(number|null)[]} x
 * @param {(number|null)[]} y
 * @param {(number|null)[]} z
 * @returns {PointCloud}
 */
module.exports.makePointCloud = (x, y, z) => {
  Joi.assert(x, Joi.array().items(Joi.number().allow(null)));
  Joi.assert(y, Joi.array().items(Joi.number().allow(null)));
  Joi.assert(z, Joi.array().items(Joi.number().allow(null)));

  if (x.length !== y.length || x.length !== z.length) {
    throw new Error('x, y, and z arrays must all be the same length');
  }

  for (let i = 0; i < x.length; i++) {
    if ((x[i] === null) !== (y[i] === null) || (x[i] === null) !== (z[i] === null)) {
      throw new Error('if one entry is null all 3 entries must be null for a point');
    }
  }

  const array = zeros([x.length, 4]);
  for (let i = 0; i < x.length; i++) {
    array.set(i, 0, numberOrNaN(x[i])); // nulls are stored as NaN
    array.set(i, 1, numberOrNaN(y[i]));
    array.set(i, 2, numberOrNaN(z[i]));
    array.set(i, 3, 1);
  }
  return new PointCloud(array);
};

/**
 * @param value
 * @returns {boolean}
 */
module.exports.isPointCloud = (value) => value instanceof PointCloud;

function numberOrNaN(value) {
  return Number.isFinite(value) ? value : NaN;
}


function numberOrNull(value) {
  // NaN !== NaN by spec. https://stackoverflow.com/a/16988441. This is much faster than Number.isNaN or Number.isFinite,
  // at least in browsers. https://measurethat.net/Benchmarks/Show/31693/0/1d170c5a-bf42-4104-b3a4-9ccbdcb4f20b
  // eslint-disable-next-line no-self-compare
  return (value === value) ? value : null;
}

/**
 * @typedef PointCloud
 */
class PointCloud {
  #array;

  constructor(array) {
    this.#array = array;
  }

  /**
   * @returns {number}
   */
  numPoints() {
    return this.#array.shape[0];
  }

  /**
   * @param {number} i
   * @returns {[(number|null), (number|null), (number|null)]}
   */
  point(i) {
    return [
      numberOrNull(this.#array.get(i, 0)),
      numberOrNull(this.#array.get(i, 1)),
      numberOrNull(this.#array.get(i, 2)),
    ];
  }

  transform(transform) {
    const newArray = zeros(this.#array.shape);

    const transformAsArray = zeros([4, 4]);
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        transformAsArray.set(i, j, transform.asMatrix4x4().get([j, i]));
      }
    }
    ndgemm(newArray, this.#array, transformAsArray);

    return new PointCloud(newArray);
  }

  // Null values are converted to NaN
  asMatrix4x4() {
    const nullToNaN = function (x) {
      return x === null ? NaN : x;
    };
    return math.matrix([this._x.map(nullToNaN), this._y.map(nullToNaN), this._z.map(nullToNaN), Array(this.numPoints()).fill(1)]);
  }

  /**
   * Returns the underlying arrays of points
   * @returns {{x: (number|null)[], y: (number|null)[], z: (number|null)[]}}
   */
  toArrays() {
    const unpackedArrays = ndunpack(this.#array.transpose(1, 0));
    return { x: unpackedArrays[0], y: unpackedArrays[1], z: unpackedArrays[2] };
  }
}
